import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import orderAPI from '../../api/orderAPI';
import { formatDate } from '../../utils/dateUtils';
import { formatNumber } from '../../utils/numberUtils';

const SalesOrderList = () => {
    const [salesOrders, setSalesOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchSalesOrders();
    }, [currentPage, itemsPerPage, statusFilter]); // Refetch when pagination or filters change

    // Reset to page 1 when search term changes
    useEffect(() => {
        if (searchTerm) {
            setCurrentPage(1);
        }
    }, [searchTerm]);

    const fetchSalesOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Build query parameters for backend
            const params = {
                populate: 'customer,store,cashier',
                page: currentPage,
                limit: itemsPerPage
            };

            // Add status filter if not 'all'
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            // For now, we'll handle search client-side
            // If you want backend search, you'll need to add it to your backend route

            const response = await orderAPI.getAll(params);

            //console.log('API Response:', response); // Debug log

            // Handle the response structure
            if (response.success && response.data) {
                // Transform data if needed
                const orders = response.data.map(so => ({
                    ...so,
                    _id: so._id,
                    customer: so.customer || { name: 'Walk-in' },
                    store: so.store || { name: 'N/A' },
                    cashier: so.cashier || { name: 'System' }
                }));

                setSalesOrders(orders);
                
                // Set pagination info from backend
                if (response.pagination) {
                    setTotalItems(response.pagination.totalItems || response.pagination.total || 0);
                    setTotalPages(response.pagination.totalPages || 0);
                } else {
                    // Fallback if no pagination data
                    setTotalItems(orders.length);
                    setTotalPages(1);
                }
            } else {
                throw new Error('Invalid response format from server');
            }
            
        } catch (error) {
            console.error('Error fetching sales orders:', error);
            setError(error.message || 'Failed to load sales orders');
            setSalesOrders([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Client-side search filtering
    const filteredSalesOrders = salesOrders.filter(so => {
        if (!searchTerm) return true;
        
        const lowercasedSearch = searchTerm.toLowerCase();
        return (
            (so.orderNumber?.toLowerCase() || '').includes(lowercasedSearch) ||
            (so.customer?.name?.toLowerCase() || '').includes(lowercasedSearch) ||
            (so.store?.name?.toLowerCase() || '').includes(lowercasedSearch) ||
            (so.cashier?.name?.toLowerCase() || '').includes(lowercasedSearch) ||
            (formatDate(so.createdAt)?.includes(lowercasedSearch))
        );
    });

    // Use filtered data for display when searching
    const displayData = searchTerm ? filteredSalesOrders : salesOrders;

    const handleCancel = async () => {
        try {
            await orderAPI.cancel(selectedSalesOrder._id);
            // Refresh the data after cancellation
            fetchSalesOrders();
            setConfirmModalOpen(false);
        } catch (err) {
            setError(err.message || 'Failed to cancel sales order');
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1); // Reset to first page when changing status filter
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const statusBadgeClass = (status) => {
        // Make it case-insensitive
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'draft':
                return 'bg-gray-100 text-gray-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            case 'returned':
                return 'bg-orange-100 text-orange-800';
            case 'open':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatStatusDisplay = (status) => {
        // Capitalize first letter for display
        if (!status) return 'Unknown';
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const columns = [
        {
            title: 'SO #',
            key: 'orderNumber',
            render: (so) => (
                <span className="font-medium">{so.orderNumber}</span>
            )
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (so) => {
                if (typeof so.customer === 'object') {
                    return so.customer?.name || 'Walk-in';
                }
                return 'Walk-in';
            }
        },
        {
            title: 'Payment',
            key: 'payment',
            render: (so) => {
                if (typeof so.payment === 'object') {
                    const method = so.payment?.method || 'N/A';
                    return <span className="capitalize">{method}</span>;
                }
                return 'N/A';
            }
        },
        {
            title: 'Order Date',
            key: 'orderDate',
            render: (so) => formatDate(so.createdAt)
        },
        {
            title: 'Total',
            key: 'total',
            render: (so) => `${formatNumber(so.total) || '0.00'}`
        },
        {
            title: 'Status',
            key: 'status',
            render: (so) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(so.status)}`}>
                    {formatStatusDisplay(so.status)}
                </span>
            )
        }
    ];

    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
        const endItem = Math.min(currentPage * itemsPerPage, totalItems);

        return (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                {/* Mobile view */}
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>

                {/* Desktop view */}
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{startItem}</span> to{' '}
                            <span className="font-medium">{endItem}</span> of{' '}
                            <span className="font-medium">{totalItems}</span> results
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        {/* Items per page selector */}
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value={10}>10 per page</option>
                            <option value={25}>25 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                        </select>

                        {/* Page navigation */}
                        {totalPages > 0 && (
                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">Previous</span>
                                    &larr;
                                </button>

                                {pageNumbers.map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                            currentPage === page
                                                ? 'z-10 bg-teal-500 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500'
                                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">Next</span>
                                    &rarr;
                                </button>
                            </nav>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Layout pageTitle="Sales Orders">
            <div className="bg-gray-100 mx-auto p-6">

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                        <DebouncedSearchInput
                            placeholder="Search by order#, customer, store, cashier or date..."
                            onSearch={handleSearch}
                            className="flex-1 mr-4 max-w-md"
                        />

                        <select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value="all">All Statuses</option>
                            <option value="draft">Draft</option>
                            <option value="open">Open</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="returned">Returned</option>
                        </select>
                    </div>

                    <button 
                        onClick={() => navigate('/sales-order/new')}
                        className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center"
                    >
                        <span className="mr-1">+</span> New Sale
                    </button>
                </div>

                <div className="mb-2">
                    {searchTerm && (
                        <div className="text-sm text-gray-500">
                            Showing {filteredSalesOrders.length} sales orders matching "{searchTerm}"
                        </div>
                    )}
                    {statusFilter !== 'all' && (
                        <div className="text-sm text-gray-500">
                            Filtered by status: {statusFilter}
                        </div>
                    )}
                </div>

                {/* Data Table with Pagination */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={displayData}
                        loading={loading}
                        emptyMessage="No sales orders found"
                        onRowClick={(so) => navigate(`/sales-order/${so._id}`)}
                        onDelete={(so) => {
                            setSelectedSalesOrder(so);
                            setConfirmModalOpen(true);
                        }}
                        disableDelete={(so) => so.status !== 'completed'}
                    />
                    
                    {/* Pagination */}
                    {totalItems > 0 && <Pagination />}
                </div>

                {/* Confirm Cancel Modal */}
                {confirmModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Confirm Cancel</h2>
                                <p className="mb-6">
                                    Are you sure you want to cancel the sales order "{selectedSalesOrder?.orderNumber}"? 
                                    This action cannot be undone.
                                </p>
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={() => setConfirmModalOpen(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                    >
                                        Confirm Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SalesOrderList;